import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DataTable, EmptyState, FormField } from "@/components/ui-system";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Info, Truck, Loader2, Upload, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

const ALL_MODES = ["surface", "air"];
// Partners that always get an editor section (even when empty). Keep in sync as
// integrated partners are added — but the editor also renders any partner key
// already present in a courier's aggregator_map (see the render below), so a
// missing entry here never hides existing mappings.
const AGGREGATORS = ["shiprocket", "ithink", "expressfly"];
const PAGE_SIZE = 50;

const blankForm = () => ({
  code: "",
  name: "",
  modes: ["surface"],
  active: true,
  logo_url: "",
  aggregator_map: {}, // { shiprocket: [{courier_id, mode}], ithink: [...] }
});

/**
 * Summarize the aggregator_map as "shiprocket:2, ithink:1".
 */
const summarizeMap = (map) => {
  if (!map || typeof map !== "object") return "—";
  const parts = Object.entries(map)
    .filter(([, v]) => Array.isArray(v) && v.length)
    .map(([agg, v]) => `${agg}:${v.length}`);
  return parts.length ? parts.join(", ") : "—";
};

/**
 * Couriers tab — canonical courier registry table + add/edit dialog with an
 * aggregator_map editor.
 *
 * props: canManage (b2c.couriers:manage), onCouriersChanged(cb after add/edit so the
 *        rate-card add-row picker stays fresh)
 */
export function CourierAggregatorMapEditor({ canManage, onCouriersChanged }) {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Partner-courier catalog lookup: "{aggregator}:{courier_id}" -> human name
  // (e.g. "expressfly:69afed…" -> "Delhivery Surface E2"). Read-only, so the
  // aggregator-map editor can show a readable name beside each raw id.
  const [catalog, setCatalog] = useState({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // courier being edited, or null for create
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  // Logo upload (PNG/JPG -> S3 -> logo_url)
  const logoInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Sync couriers from partners (preview -> apply)
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncData, setSyncData] = useState(null); // { partners, mappings, already_mapped, unmapped }
  const [syncApplying, setSyncApplying] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set()); // mapKey() of chosen mappings
  const [selectedCreations, setSelectedCreations] = useState(() => new Set()); // creation.code of chosen new couriers

  // Client-side filter + pagination for the canonical registry (GET /couriers
  // returns the whole list, up to 1000).
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchCouriers = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/b2c/admin/couriers`);
      setCouriers(res.data || []);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Load the partner-courier catalog once so ids can be shown with their names.
  // Names are a display nicety — a failure here never blocks the editor.
  const fetchCatalog = async () => {
    try {
      const res = await axios.get(`${API}/b2c/admin/partner-couriers`, { params: { limit: 2000 } });
      const map = {};
      for (const it of res.data?.items || []) {
        if (it?.name) map[`${it.aggregator}:${it.aggregator_courier_id}`] = it.name;
      }
      setCatalog(map);
    } catch {
      /* ignore — ids just render without a resolved name */
    }
  };

  useEffect(() => {
    fetchCouriers();
    fetchCatalog();
  }, []);

  // Filtered + paged view. Matches across Edgistify ID, code, name and the
  // aggregator-map summary so any of them can be searched.
  const query = search.trim().toLowerCase();
  const filteredCouriers = query
    ? couriers.filter((c) =>
        [c.id, c.code, c.name, summarizeMap(c.aggregator_map)].some((v) =>
          String(v || "").toLowerCase().includes(query)
        )
      )
    : couriers;
  const totalCouriers = filteredCouriers.length;
  const totalPages = Math.max(1, Math.ceil(totalCouriers / PAGE_SIZE));
  const pagedCouriers = filteredCouriers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when the filter changes; clamp if the set shrank (search or reload).
  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code || "",
      name: c.name || "",
      modes: c.modes && c.modes.length ? c.modes : ["surface"],
      active: c.active !== false,
      logo_url: c.logo_url || "",
      aggregator_map: c.aggregator_map ? JSON.parse(JSON.stringify(c.aggregator_map)) : {},
    });
    setDialogOpen(true);
  };

  const toggleMode = (m) => {
    setForm((f) => {
      const has = f.modes.includes(m);
      const modes = has ? f.modes.filter((x) => x !== m) : [...f.modes, m];
      return { ...f, modes };
    });
  };

  const addMapRow = (agg) => {
    setForm((f) => {
      const list = Array.isArray(f.aggregator_map[agg]) ? [...f.aggregator_map[agg]] : [];
      list.push({ courier_id: "", mode: "surface" });
      return { ...f, aggregator_map: { ...f.aggregator_map, [agg]: list } };
    });
  };

  const updateMapRow = (agg, idx, field, value) => {
    setForm((f) => {
      const list = [...(f.aggregator_map[agg] || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...f, aggregator_map: { ...f.aggregator_map, [agg]: list } };
    });
  };

  const removeMapRow = (agg, idx) => {
    setForm((f) => {
      const list = (f.aggregator_map[agg] || []).filter((_, i) => i !== idx);
      const nextMap = { ...f.aggregator_map };
      if (list.length) nextMap[agg] = list;
      else delete nextMap[agg];
      return { ...f, aggregator_map: nextMap };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Code and name are required");
      return;
    }
    if (!form.modes.length) {
      toast.error("Select at least one mode");
      return;
    }
    // Drop blank aggregator-map rows, drop empty aggregator keys.
    const cleanedMap = {};
    Object.entries(form.aggregator_map).forEach(([agg, rows]) => {
      const valid = (rows || []).filter((r) => r.courier_id && String(r.courier_id).trim());
      if (valid.length) {
        cleanedMap[agg] = valid.map((r) => ({ courier_id: String(r.courier_id).trim(), mode: r.mode }));
      }
    });

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        modes: form.modes,
        active: form.active,
        logo_url: form.logo_url.trim() || undefined,
        aggregator_map: cleanedMap,
      };
      if (editing) {
        await axios.put(`${API}/b2c/admin/couriers/${editing.id}`, payload);
        toast.success("Courier updated");
      } else {
        await axios.post(`${API}/b2c/admin/couriers`, { code: form.code.trim(), ...payload });
        toast.success("Courier added");
      }
      setDialogOpen(false);
      await fetchCouriers();
      onCouriersChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
          : "Failed to save courier"
      );
    } finally {
      setSaving(false);
    }
  };

  // ---- Logo upload -------------------------------------------------------
  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Logo must be a PNG or JPG image");
      return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("Logo too large (max 1 MB)");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/b2c/admin/couriers/logo`, fd);
      setForm((f) => ({ ...f, logo_url: res.data?.url || f.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to upload logo");
    } finally {
      setLogoUploading(false);
    }
  };

  // ---- Sync couriers from partners --------------------------------------
  const mapKey = (m) => `${m.aggregator}:${m.aggregator_courier_id}:${m.mode}`;

  const openSync = async () => {
    setSyncOpen(true);
    setSyncData(null);
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API}/b2c/admin/couriers/sync-preview`);
      const data = res.data || {};
      setSyncData(data);
      // Pre-select every proposed mapping; leave NEW-courier creations unchecked
      // (creating couriers is heavier than mapping — opt-in).
      setSelectedKeys(new Set((data.mappings || []).map(mapKey)));
      setSelectedCreations(new Set());
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reach partners for sync");
      setSyncOpen(false);
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleMapping = (m) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = mapKey(m);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const allSelected =
    syncData?.mappings?.length > 0 && selectedKeys.size === syncData.mappings.length;

  const toggleAll = () => {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set((syncData?.mappings || []).map(mapKey)));
  };

  const toggleCreation = (code) => {
    setSelectedCreations((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const allCreationsSelected =
    syncData?.creations?.length > 0 && selectedCreations.size === syncData.creations.length;

  const toggleAllCreations = () => {
    if (allCreationsSelected) setSelectedCreations(new Set());
    else setSelectedCreations(new Set((syncData?.creations || []).map((c) => c.code)));
  };

  const totalSelected = selectedKeys.size + selectedCreations.size;

  const applySync = async () => {
    const chosenMappings = (syncData?.mappings || []).filter((m) => selectedKeys.has(mapKey(m)));
    const chosenCreations = (syncData?.creations || []).filter((c) => selectedCreations.has(c.code));
    if (!chosenMappings.length && !chosenCreations.length) return;
    setSyncApplying(true);
    try {
      const payload = {
        mappings: chosenMappings.map((m) => ({
          aggregator: m.aggregator,
          aggregator_courier_id: m.aggregator_courier_id,
          mode: m.mode,
          canonical_id: m.canonical_id,
        })),
        creations: chosenCreations.map((c) => ({
          code: c.code,
          name: c.name,
          modes: c.modes,
          sources: (c.sources || []).map((s) => ({
            aggregator: s.aggregator,
            aggregator_courier_id: s.aggregator_courier_id,
            mode: s.mode,
          })),
        })),
      };
      const res = await axios.post(`${API}/b2c/admin/couriers/sync-apply`, payload);
      const { applied = 0, created = 0 } = res.data || {};
      toast.success(`Mapped ${applied} courier id(s)` + (created ? `, created ${created} courier(s)` : ""));
      setSyncOpen(false);
      await fetchCouriers();
      onCouriersChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to apply sync");
    } finally {
      setSyncApplying(false);
    }
  };

  const columns = [
    { key: "id", header: "Edgistify ID", primary: true, cell: (c) => <span className="font-mono text-xs text-slate-700">{c.id}</span> },
    { key: "code", header: "Code", cell: (c) => <span className="font-mono">{c.code}</span> },
    { key: "name", header: "Name", cell: (c) => c.name },
    {
      key: "modes",
      header: "Modes",
      cell: (c) => (
        <div className="flex flex-wrap gap-1">
          {(c.modes || []).map((m) => (
            <Badge key={m} variant="outline" className="text-[10px] px-1.5 py-0">
              {m}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "active",
      header: "Active",
      cell: (c) =>
        c.active !== false ? (
          <Badge className="bg-green-100 text-green-800 border-0 text-xs">Active</Badge>
        ) : (
          <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">Inactive</Badge>
        ),
    },
    {
      key: "map",
      header: "Aggregator map",
      cell: (c) => <span className="text-xs tabular-nums text-slate-600">{summarizeMap(c.aggregator_map)}</span>,
    },
    {
      key: "actions",
      header: "",
      isActions: true,
      align: "right",
      cell: (c) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEdit(c)}
          disabled={!canManage}
          data-testid={`courier-edit-${c.code}`}
        >
          <Edit className="mr-1.5 h-4 w-4" />
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-slate-800">Canonical couriers</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex" tabIndex={0} aria-label="What is the aggregator map?">
                  <Info className="h-4 w-4 text-slate-400" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                The aggregator_map links each aggregator's courier ids to this canonical courier. It drives white-label
                dedupe — quotes from Shiprocket and iThink for the same real carrier collapse into one option.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={openSync}
            disabled={!canManage}
            data-testid="courier-sync"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Sync couriers
          </Button>
          <Button onClick={openCreate} disabled={!canManage} data-testid="courier-add">
            <Plus className="mr-2 h-4 w-4" />
            Add courier
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by Edgistify ID, code, name, or aggregator…"
          className="h-9 w-full sm:max-w-xs"
          data-testid="courier-filter"
        />
        <span className="text-xs text-slate-500" data-testid="courier-count">
          {totalCouriers} courier{totalCouriers === 1 ? "" : "s"}
          {query ? ` matching “${search.trim()}”` : ""}
        </span>
      </div>

      {error ? (
        <div className="rounded-xl border bg-white p-8 text-center">
          <p className="text-sm text-slate-600">Could not load couriers.</p>
          <Button variant="outline" className="mt-3" onClick={fetchCouriers} data-testid="courier-retry">
            Retry
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={pagedCouriers}
          rowKey={(c) => c.id || c.code}
          loading={loading}
          pagination={{
            page,
            pages: totalPages,
            total: totalCouriers,
            pageSize: PAGE_SIZE,
            onPageChange: setPage,
            mode: "numbered",
          }}
          empty={
            query && couriers.length > 0 ? (
              <EmptyState
                icon={Truck}
                title="No couriers match your filter"
                description="Try a different Edgistify ID, code, name, or aggregator."
              />
            ) : (
              <EmptyState
                icon={Truck}
                title="No couriers in the registry"
                description="Add canonical couriers so plans can build rate-card rows and aggregator quotes can dedupe."
                action={
                  canManage ? (
                    <Button onClick={openCreate} data-testid="courier-add-empty">
                      <Plus className="mr-2 h-4 w-4" />
                      Add courier
                    </Button>
                  ) : null
                }
              />
            )
          }
        />
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit courier" : "Add courier"}</DialogTitle>
            <DialogDescription>
              {editing ? (
                <>
                  Update the canonical courier and its aggregator map.{" "}
                  <span className="font-mono text-xs text-slate-500">({editing.id})</span>
                </>
              ) : (
                "Register a canonical courier — it gets an Edgistify ID automatically."
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Code" htmlFor="courier-code" required hint={editing ? "Code is locked on edit" : "Unique short code, e.g. DELHIVERY_SURF"}>
                <Input
                  id="courier-code"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  disabled={!!editing}
                  data-testid="courier-code"
                />
              </FormField>
              <FormField label="Name" htmlFor="courier-name" required>
                <Input
                  id="courier-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="courier-name"
                />
              </FormField>
            </div>

            <FormField label="Logo" htmlFor="courier-logo" hint="Optional — upload a PNG/JPG or paste a URL">
              <div className="flex items-center gap-3">
                {form.logo_url ? (
                  <img
                    src={form.logo_url}
                    alt="Courier logo preview"
                    className="h-10 w-10 flex-shrink-0 rounded border bg-white object-contain p-0.5"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded border bg-slate-50 text-slate-300">
                    <Truck className="h-5 w-5" />
                  </div>
                )}
                <Input
                  id="courier-logo"
                  value={form.logo_url}
                  onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                  placeholder="https://…"
                  className="flex-1"
                  data-testid="courier-logo"
                />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={handleLogoFile}
                  data-testid="courier-logo-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  data-testid="courier-logo-upload"
                >
                  {logoUploading ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-1.5 h-4 w-4" />
                  )}
                  Upload
                </Button>
              </div>
            </FormField>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Modes *</Label>
              <div className="flex flex-wrap gap-4">
                {ALL_MODES.map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox
                      checked={form.modes.includes(m)}
                      onCheckedChange={() => toggleMode(m)}
                      data-testid={`courier-mode-${m}`}
                    />
                    {m}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border bg-slate-50 px-3 py-2">
              <Label htmlFor="courier-active" className="text-sm font-medium text-slate-700">
                Active
              </Label>
              <Switch
                id="courier-active"
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="courier-active"
              />
            </div>

            {/* Aggregator map editor */}
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-slate-700">Aggregator map</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex" tabIndex={0} aria-label="Aggregator map help">
                        <Info className="h-3.5 w-3.5 text-slate-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-xs">
                      Map each aggregator's own courier id (+ mode) to this canonical courier. Used to dedupe
                      white-label quotes across every integrated partner.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {Array.from(new Set([...AGGREGATORS, ...Object.keys(form.aggregator_map || {})])).map((agg) => (
                <div key={agg} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{agg}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => addMapRow(agg)}
                      data-testid={`map-add-${agg}`}
                    >
                      <Plus className="mr-1 h-3 w-3" />
                      Add id
                    </Button>
                  </div>
                  {(form.aggregator_map[agg] || []).length === 0 ? (
                    <p className="text-xs italic text-slate-400">No ids mapped.</p>
                  ) : (
                    (form.aggregator_map[agg] || []).map((row, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="flex-1">
                          <Input
                            value={row.courier_id}
                            onChange={(e) => updateMapRow(agg, idx, "courier_id", e.target.value)}
                            placeholder="aggregator courier id"
                            className="h-8 w-full text-xs"
                            data-testid={`map-${agg}-id-${idx}`}
                          />
                          {row.courier_id ? (
                            catalog[`${agg}:${String(row.courier_id).trim()}`] ? (
                              <p
                                className="mt-0.5 truncate text-[11px] text-slate-500"
                                data-testid={`map-${agg}-name-${idx}`}
                                title={catalog[`${agg}:${String(row.courier_id).trim()}`]}
                              >
                                {catalog[`${agg}:${String(row.courier_id).trim()}`]}
                              </p>
                            ) : (
                              <p
                                className="mt-0.5 truncate text-[11px] italic text-slate-400"
                                title="Not found in the synced partner catalog"
                              >
                                not in {agg} catalog
                              </p>
                            )
                          ) : null}
                        </div>
                        <Select value={row.mode} onValueChange={(v) => updateMapRow(agg, idx, "mode", v)}>
                          <SelectTrigger className="h-8 w-24 text-xs" data-testid={`map-${agg}-mode-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ALL_MODES.map((m) => (
                              <SelectItem key={m} value={m}>
                                {m}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => removeMapRow(agg, idx)}
                          aria-label="Remove mapping"
                          data-testid={`map-${agg}-del-${idx}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="courier-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="courier-submit">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? "Save changes" : "Add courier"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sync couriers from partners dialog */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sync couriers from partners</DialogTitle>
            <DialogDescription>
              Discovered live from each configured partner (read-only). Review the new ID mappings,
              then apply the ones you want into the registry.
            </DialogDescription>
          </DialogHeader>

          {syncLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Reaching partners…
            </div>
          ) : !syncData ? null : (
            <div className="space-y-5">
              {/* Per-partner status */}
              <div className="flex flex-wrap gap-2">
                {(syncData.partners || []).map((p) => {
                  const ok = p.status === "ok";
                  const noCreds = p.status === "no_credentials";
                  const cls = ok
                    ? "bg-green-50 text-green-700 border-green-200"
                    : noCreds
                    ? "bg-slate-100 text-slate-600 border-slate-200"
                    : "bg-red-50 text-red-700 border-red-200";
                  return (
                    <span
                      key={p.name + p.display_name}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${cls}`}
                      title={p.message || ""}
                    >
                      {ok ? (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5" />
                      )}
                      {p.display_name}
                      {ok
                        ? ` · ${p.couriers_found} found`
                        : noCreds
                        ? " · no credentials"
                        : " · error"}
                    </span>
                  );
                })}
              </div>

              {/* New mappings to apply */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">
                    New mappings ({syncData.mappings?.length || 0})
                  </span>
                  {syncData.mappings?.length > 0 && (
                    <button
                      type="button"
                      className="text-xs font-medium text-teal-700 hover:underline"
                      onClick={toggleAll}
                      data-testid="sync-select-all"
                    >
                      {allSelected ? "Deselect all" : "Select all"}
                    </button>
                  )}
                </div>
                {syncData.mappings?.length === 0 ? (
                  <p className="rounded-lg border bg-slate-50 px-3 py-3 text-xs text-slate-500">
                    No new mappings — the registry already covers every courier the partners returned.
                  </p>
                ) : (
                  <div className="divide-y rounded-lg border">
                    {syncData.mappings.map((m) => (
                      <label
                        key={mapKey(m)}
                        className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50"
                      >
                        <Checkbox
                          checked={selectedKeys.has(mapKey(m))}
                          onCheckedChange={() => toggleMapping(m)}
                          data-testid={`sync-map-${m.aggregator}-${m.aggregator_courier_id}`}
                        />
                        <span className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {m.aggregator}
                          </Badge>
                          <span className="font-mono text-xs text-slate-500">#{m.aggregator_courier_id}</span>
                          <span className="text-slate-700">{m.courier_name_raw || "—"}</span>
                          <span className="text-slate-400">→</span>
                          <span className="font-medium text-slate-800">{m.canonical_name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {m.mode}
                          </Badge>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* New couriers to create (unmatched, grouped by base name) */}
              {syncData.creations?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">
                      New couriers to create ({syncData.creations.length})
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-teal-700 hover:underline"
                      onClick={toggleAllCreations}
                      data-testid="sync-create-select-all"
                    >
                      {allCreationsSelected ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    No canonical courier matched these. Tick the ones to add to the registry — each gets an
                    Edgistify ID and the aggregator id(s) mapped in. Variants are grouped to one courier.
                  </p>
                  <div className="divide-y rounded-lg border border-dashed">
                    {syncData.creations.map((c) => (
                      <label
                        key={c.code}
                        className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-slate-50"
                      >
                        <Checkbox
                          className="mt-0.5"
                          checked={selectedCreations.has(c.code)}
                          onCheckedChange={() => toggleCreation(c.code)}
                          data-testid={`sync-create-${c.code}`}
                        />
                        <span className="flex flex-1 flex-col gap-0.5">
                          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                            <span className="font-medium text-slate-800">{c.name}</span>
                            <span className="font-mono text-xs text-slate-400">{c.code}</span>
                            {(c.modes || []).map((m) => (
                              <Badge key={m} variant="outline" className="text-[10px]">
                                {m}
                              </Badge>
                            ))}
                          </span>
                          <span className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                            {(c.sources || []).map((s, i) => (
                              <span key={i}>
                                {s.aggregator} <span className="font-mono">#{s.aggregator_courier_id}</span>
                              </span>
                            ))}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {syncData.already_mapped?.length > 0 && (
                <p className="text-xs text-slate-400">
                  {syncData.already_mapped.length} mapping(s) already in the registry — skipped.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSyncOpen(false)} data-testid="sync-cancel">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={applySync}
              disabled={syncApplying || syncLoading || totalSelected === 0}
              data-testid="sync-apply"
            >
              {syncApplying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Apply {totalSelected > 0 ? `${totalSelected} ` : ""}change(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CourierAggregatorMapEditor;
